/**
 * SupportPoolInfoModal Component
 *
 * Displays support gacha pool contents
 * Shows which support cards the player has collected
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Info, Heart, Swords, Shield, Zap, Wind } from 'lucide-react';
import { getRarityColor } from '../utils/gameUtils';
import { useGameData } from '../contexts/GameDataContext';
import { useInventory } from '../contexts/InventoryContext';
import LimitBreakDiamonds from './LimitBreakDiamonds';
import { getSupportImageFromCardName } from '../constants/trainerImages';
import { TYPE_COLORS } from './TypeIcon';

// Helper to get focus stat color
const getFocusColor = (stat) => {
  const colors = {
    HP: '#dc2626',
    Attack: '#f97316',
    Defense: '#3b82f6',
    Instinct: '#9333ea',
    Speed: '#eab308'
  };
  return colors[stat] || '#6b7280';
};

const SupportPoolInfoModal = ({ isOpen, onClose }) => {
  const { supportInventory, getSupportLimitBreak } = useInventory();
  const { SUPPORT_CARDS, SUPPORT_GACHA_RARITY, MOVES } = useGameData();

  // Long-press state for detail modal
  const [detailSupport, setDetailSupport] = useState(null);
  const longPressTimerRef = useRef(null);
  const longPressSupportRef = useRef(null);
  const LONG_PRESS_DURATION = 500; // 500ms hold to trigger

  const handleLongPressStart = useCallback((supportName) => {
    longPressSupportRef.current = supportName;
    longPressTimerRef.current = setTimeout(() => {
      setDetailSupport(longPressSupportRef.current);
    }, LONG_PRESS_DURATION);
  }, []);

  const handleLongPressEnd = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    longPressSupportRef.current = null;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
      }
    };
  }, []);

  if (!isOpen) return null;

  // Defensive checks - data loaded from GameDataContext
  if (!SUPPORT_GACHA_RARITY || !SUPPORT_CARDS || !supportInventory) {
    return null;
  }

  // Get player's collection
  const getCollectionStatus = () => {
    const status = {
      Common: { owned: [], total: 0 },
      Uncommon: { owned: [], total: 0 },
      Rare: { owned: [], total: 0 },
      Legendary: { owned: [], total: 0 }
    };

    Object.entries(SUPPORT_GACHA_RARITY).forEach(([rarity, data]) => {
      if (!data.supports) return;

      status[rarity].total = data.supports.length;
      status[rarity].owned = data.supports.filter(supportName => supportInventory.includes(supportName));
    });

    return status;
  };

  const collectionStatus = getCollectionStatus();

  // Calculate total collection percentage
  const totalOwned = Object.values(collectionStatus).reduce((sum, { owned }) => sum + owned.length, 0);
  const totalSupports = Object.values(collectionStatus).reduce((sum, { total }) => sum + total, 0);
  const collectionPercentage = totalSupports > 0 ? Math.round((totalOwned / totalSupports) * 100) : 0;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white rounded-2xl shadow-card-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 rounded-t-2xl z-10">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-pocket-text flex items-center gap-2">
                    <Info size={20} className="text-pocket-blue" />
                    Support Card Pool
                  </h2>
                  <p className="text-sm text-pocket-text-light mt-1">
                    Collection: {totalOwned}/{totalSupports} ({collectionPercentage}%)
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 text-pocket-text-light hover:text-pocket-text hover:bg-pocket-bg rounded-lg transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Collection Progress Bar */}
              <div className="mt-3">
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${collectionPercentage}%` }}
                    transition={{ duration: 0.5, ease: 'easeOut' }}
                    className="h-full bg-gradient-to-r from-pink-400 to-pink-600"
                  />
                </div>
              </div>
            </div>

            {/* Pool Contents by Rarity */}
            <div className="p-4 space-y-4">
              {['Legendary', 'Rare', 'Uncommon', 'Common'].map(rarity => {
                const rarityData = SUPPORT_GACHA_RARITY[rarity];
                if (!rarityData || !rarityData.supports || rarityData.supports.length === 0) return null;

                const { owned, total } = collectionStatus[rarity];
                const ownedCount = owned.length;
                const ownedPercentage = total > 0 ? Math.round((ownedCount / total) * 100) : 0;

                return (
                  <div key={rarity} className="bg-pocket-bg rounded-xl p-4">
                    {/* Rarity Header */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span
                          className="px-3 py-1 rounded-full text-white text-sm font-bold"
                          style={{ backgroundColor: getRarityColor(rarity) }}
                        >
                          {rarity}
                        </span>
                        <span className="text-xs text-pocket-text-light">
                          {(rarityData.rate * 100).toFixed(1)}% drop rate
                        </span>
                      </div>
                      <div className="text-sm font-semibold text-pocket-text">
                        {ownedCount}/{total} ({ownedPercentage}%)
                      </div>
                    </div>

                    {/* Support Grid */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                      {rarityData.supports.map(supportName => {
                        const isOwned = supportInventory.includes(supportName);
                        const supportData = SUPPORT_CARDS[supportName];
                        const limitBreak = isOwned ? getSupportLimitBreak(supportName) : 0;

                        return (
                          <motion.div
                            key={supportName}
                            whileHover={{ scale: 1.02 }}
                            onMouseDown={() => handleLongPressStart(supportName)}
                            onMouseUp={handleLongPressEnd}
                            onMouseLeave={handleLongPressEnd}
                            onTouchStart={() => handleLongPressStart(supportName)}
                            onTouchEnd={handleLongPressEnd}
                            className={`relative rounded-lg p-3 transition-all cursor-pointer ${
                              isOwned
                                ? 'bg-white border-2 border-pink-400 shadow-sm'
                                : 'bg-gray-100 border-2 border-gray-300 opacity-40'
                            }`}
                            title={supportName}
                          >
                            {/* NEW badge for unowned */}
                            {!isOwned && (
                              <div className="absolute -top-1 -right-1 z-10">
                                <span className="bg-red-500 text-white text-[8px] font-bold px-1 rounded">
                                  NEW
                                </span>
                              </div>
                            )}

                            {/* Trainer Sprite */}
                            {getSupportImageFromCardName(supportName) && (
                              <div className="flex justify-center mb-2">
                                <img
                                  src={getSupportImageFromCardName(supportName)}
                                  alt={supportData?.trainer || supportName}
                                  className={`w-16 h-16 object-cover rounded-full border-2 ${
                                    !isOwned ? 'grayscale' : ''
                                  }`}
                                  style={{ borderColor: getRarityColor(rarity) }}
                                />
                              </div>
                            )}

                            {/* Support Info */}
                            <div className="text-center">
                              <p className={`text-xs font-bold mb-1 ${!isOwned ? 'text-gray-400' : 'text-pocket-text'}`}>
                                {supportName}
                              </p>
                              {supportData && (
                                <p className={`text-[10px] ${!isOwned ? 'text-gray-400' : 'text-pocket-text-light'}`}>
                                  {supportData.archetype || 'Support'}
                                </p>
                              )}
                            </div>

                            {/* Limit Break Diamonds (only if owned) */}
                            {isOwned && (
                              <div className="flex justify-center mt-2">
                                <LimitBreakDiamonds level={limitBreak} size={6} />
                              </div>
                            )}
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 rounded-b-2xl">
              <button
                onClick={onClose}
                className="w-full py-3 rounded-xl bg-pink-500 text-white font-semibold hover:bg-pink-600 transition-colors"
              >
                Close
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Support Detail Modal (Long Press) */}
      {detailSupport && SUPPORT_CARDS[detailSupport] && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-60 z-[60] flex items-center justify-center p-4"
          onClick={() => setDetailSupport(null)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white rounded-2xl shadow-card-lg p-5 max-w-md w-full max-h-[90vh] overflow-y-auto relative"
            onClick={e => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setDetailSupport(null)}
              className="absolute top-3 right-3 p-2 text-pocket-text-light hover:text-pocket-text rounded-lg z-10"
            >
              <X size={20} />
            </button>

            {(() => {
              const supportData = SUPPORT_CARDS[detailSupport];
              const limitBreakLevel = getSupportLimitBreak(detailSupport);
              const isOwned = supportInventory.includes(detailSupport);

              return (
                <>
                  {/* Support Header */}
                  <div className="text-center mb-4">
                    <span
                      className="px-2 py-0.5 rounded-full text-[10px] font-bold text-white inline-block mb-2"
                      style={{ backgroundColor: getRarityColor(supportData.rarity) }}
                    >
                      {supportData.rarity}
                    </span>
                    {getSupportImageFromCardName(detailSupport) && (
                      <img
                        src={getSupportImageFromCardName(detailSupport)}
                        alt={supportData.trainer}
                        className={`w-24 h-24 object-cover rounded-full border-4 mx-auto mb-3 ${
                          !isOwned ? 'grayscale' : ''
                        }`}
                        style={{ borderColor: getRarityColor(supportData.rarity) }}
                      />
                    )}
                    <h3 className="font-bold text-xl text-pocket-text">{supportData.name}</h3>
                    {isOwned && (
                      <div className="flex justify-center mt-2">
                        <LimitBreakDiamonds level={limitBreakLevel} size={14} />
                      </div>
                    )}
                    {!isOwned && (
                      <p className="text-xs text-gray-500 italic mt-2">Not yet obtained</p>
                    )}
                    <div className="flex items-center justify-center gap-3 mt-2">
                      {supportData.supportType && (
                        <span
                          className="text-sm font-bold"
                          style={{ color: getFocusColor(supportData.supportType) }}
                        >
                          Focus: {supportData.supportType}
                        </span>
                      )}
                      {supportData.type && (
                        <span
                          className="px-2 py-0.5 rounded text-xs font-bold text-white"
                          style={{ backgroundColor: TYPE_COLORS[supportData.type] || '#A8A878' }}
                        >
                          {supportData.type} Moves
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-xs text-pocket-text-light italic mb-4 text-center">
                    {supportData.description}
                  </p>

                  {/* Base Stats */}
                  {supportData.baseStats && Object.values(supportData.baseStats).some(v => v > 0) && (
                    <div className="bg-pocket-bg rounded-lg p-3 mb-3">
                      <p className="font-bold text-type-psychic text-xs mb-2">Base Stat Bonuses</p>
                      <div className="space-y-1 text-xs">
                        {Object.entries(supportData.baseStats).map(([stat, value]) => (
                          value > 0 && (
                            <div key={stat} className="flex justify-between">
                              <span className="text-pocket-text-light">{stat}</span>
                              <span className="text-pocket-green font-bold">+{value}</span>
                            </div>
                          )
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Training Bonuses */}
                  {supportData.trainingBonus && (
                    <div className="bg-pocket-bg rounded-lg p-3 mb-3">
                      <p className="font-bold text-type-psychic text-xs mb-2">Training Bonuses</p>
                      <div className="grid grid-cols-2 gap-1 text-xs">
                        <div className="flex justify-between">
                          <span className="text-pocket-text-light">Friendship</span>
                          <span className="text-pocket-blue font-bold">{supportData.initialFriendship}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-pocket-text-light">Type Match</span>
                          <span className="text-pocket-green font-bold">+{supportData.trainingBonus.typeMatch}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-pocket-text-light">Other Stats</span>
                          <span className="text-pocket-green font-bold">+{supportData.trainingBonus.otherStats}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-pocket-text-light">Max Friend</span>
                          <span className="text-pocket-green font-bold">+{supportData.trainingBonus.maxFriendshipTypeMatch}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Appearance Rate & Type Match Preference */}
                  <div className="bg-pocket-bg rounded-lg p-3 mb-3">
                    <div className="grid grid-cols-2 gap-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-pocket-text-light">Appearance</span>
                        <span className="text-pocket-text font-bold">{Math.round(supportData.appearanceRate * 100)}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-pocket-text-light">Type Pref</span>
                        <span className="text-pocket-text font-bold">{Math.round(supportData.typeMatchPreference * 100)}%</span>
                      </div>
                    </div>
                  </div>

                  {/* Special Effects */}
                  {supportData.specialEffect && (
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-3">
                      <p className="font-bold text-purple-700 text-xs mb-2">Special Effects</p>
                      <div className="space-y-1 text-xs">
                        {supportData.specialEffect.statGainMultiplier && (
                          <div className="flex justify-between">
                            <span className="text-pocket-text-light">Stat Gain</span>
                            <span className="text-pocket-green font-bold">{supportData.specialEffect.statGainMultiplier}x</span>
                          </div>
                        )}
                        {supportData.specialEffect.failRateReduction && (
                          <div className="flex justify-between">
                            <span className="text-pocket-text-light">Fail Rate</span>
                            <span className="text-pocket-green font-bold">-{(supportData.specialEffect.failRateReduction * 100).toFixed(0)}%</span>
                          </div>
                        )}
                        {supportData.specialEffect.maxEnergyBonus && (
                          <div className="flex justify-between">
                            <span className="text-pocket-text-light">Max Energy</span>
                            <span className="text-pocket-green font-bold">+{supportData.specialEffect.maxEnergyBonus}</span>
                          </div>
                        )}
                        {supportData.specialEffect.restBonus && (
                          <div className="flex justify-between">
                            <span className="text-pocket-text-light">Rest Bonus</span>
                            <span className="text-pocket-green font-bold">+{supportData.specialEffect.restBonus}</span>
                          </div>
                        )}
                        {supportData.specialEffect.energyRegenBonus && (
                          <div className="flex justify-between">
                            <span className="text-pocket-text-light">Energy Regen</span>
                            <span className="text-pocket-green font-bold">+{supportData.specialEffect.energyRegenBonus}</span>
                          </div>
                        )}
                        {supportData.specialEffect.skillPointMultiplier && (
                          <div className="flex justify-between">
                            <span className="text-pocket-text-light">SP Mult</span>
                            <span className="text-pocket-green font-bold">{supportData.specialEffect.skillPointMultiplier}x</span>
                          </div>
                        )}
                        {supportData.specialEffect.friendshipGainBonus && (
                          <div className="flex justify-between">
                            <span className="text-pocket-text-light">Friendship Gain</span>
                            <span className="text-pocket-green font-bold">+{supportData.specialEffect.friendshipGainBonus}</span>
                          </div>
                        )}
                        {supportData.specialEffect.energyCostReduction && (
                          <div className="flex justify-between">
                            <span className="text-pocket-text-light">Energy Cost</span>
                            <span className="text-pocket-green font-bold">-{supportData.specialEffect.energyCostReduction}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Move Hints */}
                  {(supportData.moveHints?.length > 0 || supportData.eventMoveHint || supportData.hangoutMoveHint) && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-3">
                      <p className="font-bold text-gray-700 text-xs mb-2">Move Hints</p>
                      <div className="flex flex-wrap gap-1">
                        {/* Training hints */}
                        {supportData.moveHints?.map((move, moveIdx) => {
                          const moveData = MOVES && MOVES[move];
                          const moveType = moveData?.type || 'Normal';
                          const typeColor = TYPE_COLORS[moveType] || '#A8A878';
                          return (
                            <span
                              key={moveIdx}
                              className="px-1.5 py-0.5 rounded text-xs font-semibold text-white"
                              style={{ backgroundColor: typeColor }}
                              title="Training hint"
                            >
                              {move}
                            </span>
                          );
                        })}
                        {/* Chain event hint */}
                        {supportData.eventMoveHint && (
                          <span
                            className="px-1.5 py-0.5 rounded text-xs font-semibold text-white"
                            style={{ backgroundColor: TYPE_COLORS[MOVES && MOVES[supportData.eventMoveHint]?.type] || '#A8A878' }}
                            title="Event hint"
                          >
                            {supportData.eventMoveHint}
                          </span>
                        )}
                        {/* Hangout event hint */}
                        {supportData.hangoutMoveHint && (
                          <span
                            className="px-1.5 py-0.5 rounded text-xs font-semibold text-white"
                            style={{ backgroundColor: TYPE_COLORS[MOVES && MOVES[supportData.hangoutMoveHint]?.type] || '#A8A878' }}
                            title="Hangout hint"
                          >
                            {supportData.hangoutMoveHint}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Close Button */}
                  <button
                    onClick={() => setDetailSupport(null)}
                    className="w-full py-2 rounded-xl bg-pink-500 text-white font-semibold hover:bg-pink-600 transition-colors"
                  >
                    Close
                  </button>
                </>
              );
            })()}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SupportPoolInfoModal;
